import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useRecordings } from '../../hooks/useRecordings';
import { useRecordingActions } from '../../hooks/useRecordingActions';
import { componentClasses as baseComponentClasses } from '../livestreams/utils/livestreamDesignSystem';
import RecordingCard from './RecordingCard';

// Modern Tailwind classes with brand colors for recording management
const componentClasses = {
  ...baseComponentClasses,
  // Enhanced management page styles with brand colors
  page: "min-h-screen bg-gradient-to-br from-gray-50 via-white to-[#00a693]/5",
  header: {
    container: "bg-white/80 backdrop-blur-sm shadow-lg border-b border-[#00a693]/20",
    title: "text-4xl font-bold text-[#010101] mb-2 bg-gradient-to-r from-[#010101] to-[#00a693] bg-clip-text text-transparent",
    subtitle: "text-lg text-[#666666]"
  },
  loading: "flex flex-col items-center justify-center min-h-[400px] bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-[#00a693]/10",
  error: "text-center py-12 bg-red-50/80 backdrop-blur-sm border border-[#BD2026]/20 rounded-2xl shadow-lg",
  empty: "text-center py-12 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-[#00a693]/10",
  livestreamGrid: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8",
  filterContainer: "flex flex-wrap gap-4 p-6 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-[#00a693]/10",
  filterInput: "px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00a693] focus:border-[#00a693] transition-all duration-300 hover:border-[#00a693]/50",
  button: {
    primary: "px-6 py-3 bg-[#00a693] text-white rounded-xl hover:bg-[#008a7a] hover:shadow-lg hover:shadow-[#00a693]/25 transition-all duration-300 hover:-translate-y-1 active:translate-y-0 font-semibold text-sm tracking-wide",
    secondary: "px-6 py-3 bg-gray-100 text-[#010101] rounded-xl hover:bg-gray-200 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 font-medium text-sm"
  }
};

const RecordingManagement = () => {
  const user = useSelector((state) => state.auth.user);
  const { recordings, loading, error, pagination, changePage, changePageSize } = useRecordings();
  
  // Ensure pagination has required properties
  const safePagination = {
    page: pagination?.page || 0,
    size: pagination?.size || 20,
    totalElements: pagination?.totalElements || 0,
    totalPages: pagination?.totalPages || 0
  };
  const {
    purchasingId,
    downloadingId,
    deletingId,
    processingId,
    handlePurchase,
    handleWatch,
    handleDownload,
    handleRename,
    handleDelete,
    handleTrimCrop,
    monitorProcessingStatus
  } = useRecordingActions();

  // Add error boundary for the component
  if (error && !loading) {
    console.error('RecordingManagement Error:', error);
  }

  // Debug logging (can be removed in production)
  console.log('🔍 RecordingManagement - Current state:', {
    userRole: user?.roles?.[0],
    recordingsLength: recordings?.length,
    loading,
    error
  });

  const [filters, setFilters] = useState({
    searchQuery: '',
    dateAfter: '',
    dateBefore: '',
    courtName: '',
    purchased: 'all' // 'all', 'purchased', 'unpurchased'
  });

  const [sortBy, setSortBy] = useState('recordingDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // User role for display purposes
  const userRole = user?.roles?.[0];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    changePage(0);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    changePage(0);
  };

  // Ensure recordings is an array and add defensive programming
  const safeRecordings = Array.isArray(recordings) ? recordings : [];

  // Filter and sort recordings with error handling
  let filteredRecordings = [];
  let sortedRecordings = [];

  try {
    // Filter recordings based on current filters
    filteredRecordings = safeRecordings.filter(recording => {
      try {
        if (!recording || typeof recording !== 'object') return false;
        
        const matchesSearch = !filters.searchQuery || 
          recording.title?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          recording.livestreamTitle?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          recording.courtName?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          recording.cameraName?.toLowerCase().includes(filters.searchQuery.toLowerCase());

        const matchesDateAfter = !filters.dateAfter || 
          (recording.recordingDate && new Date(recording.recordingDate) >= new Date(filters.dateAfter));

        const matchesDateBefore = !filters.dateBefore || 
          (recording.recordingDate && new Date(recording.recordingDate) <= new Date(filters.dateBefore));

        const matchesCourt = !filters.courtName || 
          recording.courtName?.toLowerCase().includes(filters.courtName.toLowerCase());

        const matchesPurchaseStatus = filters.purchased === 'all' ||
          (filters.purchased === 'purchased' && recording.purchased) ||
          (filters.purchased === 'unpurchased' && !recording.purchased);

        return matchesSearch && matchesDateAfter && matchesDateBefore && 
               matchesCourt && matchesPurchaseStatus;
      } catch (filterError) {
        console.warn('Error filtering recording:', recording, filterError);
        return false;
      }
    });

    // Sort filtered recordings
    sortedRecordings = [...filteredRecordings].sort((a, b) => {
      try {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Handle different data types
        if (sortBy === 'recordingDate' || sortBy === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      } catch (sortError) {
        console.warn('Error sorting recordings:', sortError);
        return 0;
      }
    });
  } catch (error) {
    console.error('Error in filtering/sorting logic:', error);
    filteredRecordings = safeRecordings;
    sortedRecordings = safeRecordings;
  }

  const handlePageChange = (newPage) => {
    changePage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    changePageSize(newSize);
  };

  if (loading) {
    return (
      <div className={componentClasses.page}>
        <div className={componentClasses.loading}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p>Loading recordings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={componentClasses.page}>
        <div className={componentClasses.error}>
          <div className="text-4xl mb-4">⚠️</div>
          <h3>Error Loading Recordings</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className={componentClasses.button.primary}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={componentClasses.page}>
      {/* Header */}
      <div className={componentClasses.header}>
        <div className="flex items-center gap-3">
          <div className="text-3xl">🎥</div>
          <div>
            <h1 className={componentClasses.header.title}>
              {userRole === 'ADMIN' ? 'All Recordings' : 
               userRole === 'SITE_OWNER' ? 'My Recordings' : 
               'My Purchased Recordings'}
            </h1>
            <p className={componentClasses.header.subtitle}>
              {safePagination.totalElements} recording{safePagination.totalElements !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="space-y-4">
        {/* Search and Basic Filters */}
        <div className={componentClasses.filterContainer}>
          <input
            type="text"
            placeholder="Search recordings, courts, cameras..."
            className={componentClasses.filterInput}
            value={filters.searchQuery || ''}
            onChange={(e) => handleFilterChange({ ...filters, searchQuery: e.target.value })}
          />
          
          <input
            type="text"
            placeholder="Filter by court..."
            className={componentClasses.filterInput}
            value={filters.courtName || ''}
            onChange={(e) => handleFilterChange({ ...filters, courtName: e.target.value })}
          />
          
          <select
            className={componentClasses.filterInput}
            value={filters.purchased}
            onChange={(e) => handleFilterChange({ ...filters, purchased: e.target.value })}
          >
            <option value="all">All Recordings</option>
            <option value="purchased">Purchased Only</option>
            <option value="unpurchased">Unpurchased Only</option>
          </select>
          
          <button
            onClick={() => handleFilterChange({ 
              searchQuery: '', 
              dateAfter: '', 
              dateBefore: '', 
              courtName: '',
              purchased: 'all'
            })}
            className={componentClasses.button.secondary}
          >
            🗑️ Clear Filters
          </button>
        </div>

        {/* Date Range Filters */}
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <input
            type="date"
            className={componentClasses.filterInput}
            value={filters.dateAfter || ''}
            onChange={(e) => handleFilterChange({ ...filters, dateAfter: e.target.value })}
            placeholder="From date"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            className={componentClasses.filterInput}
            value={filters.dateBefore || ''}
            onChange={(e) => handleFilterChange({ ...filters, dateBefore: e.target.value })}
            placeholder="To date"
          />
        </div>

        {/* Sorting Controls */}
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium text-gray-700">Sort by:</label>
          <select
            className={componentClasses.filterInput}
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="recordingDate">Recording Date</option>
            <option value="title">Title</option>
            <option value="courtName">Court</option>
            <option value="duration">Duration</option>
            <option value="size">File Size</option>
            <option value="price">Price</option>
            <option value="createdAt">Created Date</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className={componentClasses.button.secondary}
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'Asc' : 'Desc'}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600">
          Showing {sortedRecordings.length} of {safeRecordings.length} recordings
          {filters.searchQuery && ` matching "${filters.searchQuery}"`}
        </div>
        <div className="text-sm text-gray-600">
          Sorted by {sortBy} ({sortOrder === 'asc' ? 'ascending' : 'descending'})
        </div>
      </div>

      {/* Recordings Grid */}
      {safeRecordings.length === 0 ? (
        <div className={componentClasses.empty}>
          <div className="text-6xl mb-4">📹</div>
          <h3>No Recordings Found</h3>
          <p>
            {userRole === 'ADMIN' ? 'No recordings found in the system.' :
             userRole === 'SITE_OWNER' ? 'You haven\'t created any recordings yet.' :
             'You haven\'t purchased any recordings yet.'}
          </p>
        </div>
      ) : sortedRecordings.length === 0 ? (
        <div className={componentClasses.empty}>
          <div className="text-6xl mb-4">🔍</div>
          <h3>No Recordings Match Your Filters</h3>
          <p>Try adjusting your search criteria or clearing the filters.</p>
          <button
            onClick={() => handleFilterChange({ 
              searchQuery: '', 
              dateAfter: '', 
              dateBefore: '', 
              courtName: '',
              purchased: 'all'
            })}
            className={componentClasses.button.primary}
          >
            Clear All Filters
          </button>
        </div>
      ) : (
        <div className={componentClasses.livestreamGrid}>
          {sortedRecordings.map((recording) => (
            <RecordingCard
              key={recording.id}
              recording={recording}
              user={user}
              onPurchase={handlePurchase}
              onWatch={handleWatch}
              onDownload={handleDownload}
              onRename={handleRename}
              onDelete={handleDelete}
              onTrimCrop={handleTrimCrop}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {safePagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => handlePageChange(safePagination.page - 1)}
            disabled={safePagination.page === 0}
            className={componentClasses.button.secondary}
          >
            ← Previous
          </button>
          
          <span className="text-gray-600">
            Page {safePagination.page + 1} of {safePagination.totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(safePagination.page + 1)}
            disabled={safePagination.page >= safePagination.totalPages - 1}
            className={componentClasses.button.secondary}
          >
            Next →
          </button>
        </div>
      )}

      {/* Page Size Selector */}
      <div className="flex justify-center items-center gap-2 mt-4">
        <span className="text-gray-600">Show:</span>
        <select
          value={safePagination.size}
          onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span className="text-gray-600">per page</span>
      </div>
    </div>
  );
};

export default RecordingManagement;
